import { ArrowLeft, BellRing, Bug, ChevronDown, Menu, Moon, RotateCcw, Sun } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate, type RouteObject } from 'react-router-dom';
import { AIFab } from '@/components/layout/AIFab';
import { AISpotlight } from '@/components/layout/AISpotlight';
import { FloatingSearchDock } from '@/components/layout/FloatingSearchDock';
import { NotificationsDrawer } from '@/components/layout/NotificationsDrawer';
import { AppMenu } from '@/components/layout/shell/AppMenu';
import { BootScene } from '@/components/layout/shell/BootScene';
import { MainMenu } from '@/components/layout/shell/MainMenu';
import { SectionMenu } from '@/components/layout/shell/SectionMenu';
import { ShellEntrance, type EntrancePace } from '@/components/layout/shell/ShellEntrance';
import { SideZone, type SideMode } from '@/components/layout/shell/SideZone';
import {
  clearEntrance,
  peekEntrance,
  prefersReducedMotion,
  type EntranceFrom,
} from '@/components/layout/shell/entrance';
import { ConnectionStrip } from '@/components/layout/workspace/ConnectionStrip';
import type { TabCrash } from '@/components/layout/workspace/types';
import { WorkspaceCanvas } from '@/components/layout/workspace/WorkspaceCanvas';
import { WorkspaceProvider } from '@/components/layout/workspace/WorkspaceProvider';
import { focusedPath } from '@/components/layout/workspace/workspaceReducer';
import { useWorkspace } from '@/components/layout/workspace/workspaceContext';
import { ObserverProvider } from '@/components/observer/ObserverProvider';
import {
  assistantMode,
  assistantPatch,
  useObserver,
  type AssistantMode,
  type ObserverSeat,
} from '@/components/observer/observerContext';
import { isAnchorVisible, toneOf } from '@/components/observer/observerEvents';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  IconButton,
  Switch,
  Tabs,
  TabsList,
  TabsTrigger,
  ToastProvider,
  Tooltip,
  cn,
  useToast,
} from '@/components/ui';
import { useTheme } from '@/theme/useTheme';
import {
  HOME,
  INFRA_APP,
  INFRA_GROUPS,
  INFRA_MANAGE,
  INFRA_PIN,
  SECTIONS,
  SECTION_DEFS,
  SAMPLE_OFF,
  SERVICE_ORDER,
  TOOLS,
  at,
  infraPath,
  marketplaceGroups,
  menuKeyOf,
  resolveFoundation,
} from './model';
import { APP_OPEN_NAME, APP_OPEN_STEPS, APP_STEP_MS, FAST_MENU_MS, MENU_STEP } from './appOpen';
import { BOOT_FAILURE, BOOT_FAIL_STEP, BOOT_STEPS, BOOT_STEP_MS } from './boot';
import {
  PrefsContext,
  usePrefs,
  type FoundationPrefs,
  type FoundationPrefsApi,
  type LoadPace,
  type PrefsPatch,
  type TabDemo,
} from './prefs';
import { HomeScreen } from './screens/HomeScreen';
import { MarketplaceScreen, ModuleScreen } from './screens/MarketplaceScreen';
import { MyServicesScreen } from './screens/MyServicesScreen';
import { SettingsScreen } from './screens/SettingsScreen';
import { StateGate } from './screens/StateGate';
import { NotFoundScreen } from './screens/StateScreens';
import { IncidentScreen, NodeScreen } from './screens/infra/InfraObjects';
import { InfraScreen } from './screens/infra/InfraScreen';
import {
  ASK_ANCHORS,
  ASK_KEY,
  ASK_PATH,
  NOTIFY_ANCHOR,
  OP_ANCHORS,
  OP_KEY,
  OP_PATH,
  SAMPLE_OP_MS,
  emitSample,
  onSampleFallback,
} from './sampleEvents';
import { ShellCommandsContext, type ShellCommands } from './shellCommands';

/*
  /design/foundation — «Основа»: сайт целиком на примерных данных (docs/foundation.md). Каркас и
  движение — основного сайта, вид — витрины, механика вкладок — дизайн-системы. Основной сайт
  собирается из тех же деталей (components/layout/shell). Сверху — переключатели спорных мест,
  как на витрине: решение глазами.

  Помощник (spec-3 «Ядро-вестник»): оболочка — внутри ObserverProvider home="core" со своим ключом
  настроек (площадка /design/observer её не выключает). Дом — ядро знака и выделение главного меню;
  события пускает ряд «Помощник» на верхней полосе (sampleEvents.ts), признаки ставит оболочка.
*/

/**
 * Каждый вид — под StateGate: образцы «Вкладка: Не найдено · Нет доступа», проба «Уронить вкладку»; у
 * вкладок приложения — ещё и рамка холодного открытия (R42).
 */
const gated = (element: ReactNode) => <StateGate>{element}</StateGate>;
const gatedApp = (element: ReactNode) => <StateGate app>{element}</StateGate>;

const routes: RouteObject[] = [
  { path: '/', element: gated(<HomeScreen />) },
  { path: '/marketplace/m/:id', element: gated(<ModuleScreen />) },
  // «Мои сервисы» — вид раздела (веер сервисов), раньше общего списка каталога.
  { path: '/marketplace/installed', element: gated(<MyServicesScreen />) },
  { path: '/marketplace/*', element: gated(<MarketplaceScreen />) },
  { path: '/settings/*', element: gated(<SettingsScreen />) },
  { path: '/apps/infra/node/:id', element: gatedApp(<NodeScreen />) },
  { path: '/apps/infra/incident/:id', element: gatedApp(<IncidentScreen />) },
  { path: '/apps/infra/*', element: gatedApp(<InfraScreen />) },
  // Неизвестный адрес — «не найдено», а не главная (R42).
  { path: '*', element: gated(<NotFoundScreen />) },
];

/** Образцы «Сервисы» на верхней полосе: сколько подключено, по порядку SERVICE_ORDER. */
const SERVICE_SAMPLES = [
  { value: '0', label: 'Нет' },
  { value: '2', label: '2' },
  { value: '6', label: '6' },
  { value: '12', label: '12' },
  { value: '24', label: '24' },
  { value: '30', label: '30' },
];

/** Образец состояния активной вкладки (ряд «Ожидание и ошибки»). */
const TAB_DEMOS: { value: TabDemo; label: string }[] = [
  { value: 'as-is', label: 'Как есть' },
  { value: 'missing', label: 'Не найдено' },
  { value: 'locked', label: 'Нет доступа' },
];

/** Темп загрузки образца: холодное открытие приложения и данные видов (R42). */
const LOAD_PACES: { value: LoadPace; label: string }[] = [
  { value: 'fast', label: 'Быстро' },
  { value: 'slow', label: 'Медленно' },
  { value: 'fail', label: 'Сбой' },
];

/** Сколько ждёт проба «Уронить вкладку», пока её не поймали: лежащая вкладка не упадёт второй раз потом. */
const CRASH_WINDOW_MS = 500;

/** Образец «данные на …» в строке «Нет связи» (spec-2 §7). */
const OFFLINE_DATA_AT = '14:32';

/** Первая загрузка идёт: сколько шагов пройдено, встала ли (темп «Сбой»). */
interface BootRun {
  startedAt: number;
  /** Темп «Сбой»: встаёт на шаге BOOT_FAIL_STEP. */
  fail: boolean;
  done: number;
  failed: boolean;
  /** После «Повторить» сбой не повторяется. */
  retried: boolean;
  /** Попытка: «Повторить» начинает текущий шаг заново — и после сбоя, и «дольше обычного». */
  attempt: number;
}

/** Переход в рабочее место: после страницы входа (scene) или после сцены первой загрузки (brisk). */
interface Arrival {
  from: EntranceFrom;
  pace: EntrancePace;
}

/** Открытие приложения идёт: сколько шагов пройдено, встало ли, держит ли меню заглушку. */
interface AppRun {
  service: string;
  pace: LoadPace;
  done: number;
  failed: boolean;
  /** После «Повторить» сбой не повторяется. */
  retried: boolean;
  /** «Быстро»: пункты меню едут 650 мс, даже когда шаги уже пройдены. */
  menuHeld: boolean;
  /** Попытка: «Повторить» начинает текущий шаг заново — и после сбоя, и «дольше обычного». */
  attempt: number;
}

/** «Состояние системы» — долгое состояние помощника; «Готово» — событие, а не состояние (ряд «Помощник»). */
type SystemState = 'idle' | 'update' | 'error';
const SYSTEM_STATES: { value: SystemState; label: string }[] = [
  { value: 'idle', label: 'Покой' },
  { value: 'update', label: 'Операция' },
  { value: 'error', label: 'Сбой' },
];

/** Режим помощника (spec-3 §9). */
const ASSISTANT_MODES: { value: AssistantMode; label: string }[] = [
  { value: 'off', label: 'Выкл' },
  { value: 'home', label: 'На месте' },
  { value: 'guide', label: 'Показывает, где' },
];

/** Посадка тела на блок — вопрос Максиму (spec-3-motion §2); по умолчанию левое плечо. */
const SEATS: { value: ObserverSeat; label: string }[] = [
  { value: 'shoulder', label: 'Левое плечо' },
  { value: 'corner', label: 'Правый угол' },
];

/** Оверлеи оболочки: поиск, уведомления, ИИ. Живут выше оболочки — пока открыты, помощник не летает. */
interface Overlays {
  search: boolean;
  setSearch: (v: boolean | ((v: boolean) => boolean)) => void;
  notifications: boolean;
  setNotifications: (v: boolean | ((v: boolean) => boolean)) => void;
  ai: boolean;
  setAi: (v: boolean | ((v: boolean) => boolean)) => void;
}

export function Foundation() {
  const [prefs, setPrefs] = useState<FoundationPrefs>({
    sectionTone: 'dark',
    dense: false,
    focus: false,
    colorByState: true,
    pinnedInfra: true,
    services: ['infra', 'slack'],
    servicesOff: [],
    serviceAlert: false,
    seenServices: ['infra', 'slack'],
    fanSeen: ['infra', 'slack'],
    tabDemo: 'as-is',
    loadPace: 'fast',
    crashAt: null,
    offline: false,
    bootAt: null,
  });
  const set = useCallback(
    (patch: PrefsPatch) => setPrefs((p) => ({ ...p, ...(typeof patch === 'function' ? patch(p) : patch) })),
    [],
  );
  const api = useMemo<FoundationPrefsApi>(() => ({ ...prefs, set }), [prefs, set]);
  const [search, setSearch] = useState(false);
  const [notifications, setNotifications] = useState(false);
  const [ai, setAi] = useState(false);
  const overlays: Overlays = { search, setSearch, notifications, setNotifications, ai, setAi };

  return (
    <PrefsContext.Provider value={api}>
      <ToastProvider>
        <div className="flex h-screen flex-col bg-frame text-fg">
          {/* Верхняя полоса — внутри рабочего пространства: «Уронить вкладку» целится в активную вкладку. */}
          <WorkspaceProvider
            resolve={resolveFoundation}
            homePath={HOME}
            storageKey="kaname.foundation.workspace.v1"
          >
            <ObserverProvider
              home="core"
              storageKey="kaname.foundation.observer.v2"
              suspended={search || notifications || ai}
            >
              <TopBar />
              <Shell overlays={overlays} />
            </ObserverProvider>
          </WorkspaceProvider>
        </div>
      </ToastProvider>
    </PrefsContext.Provider>
  );
}

function TopBar() {
  const prefs = usePrefs();
  const ws = useWorkspace();
  const observer = useObserver();
  const { mode, toggle } = useTheme();
  const count = String(prefs.services.length);
  const system = observer.holds.system;
  const systemState: SystemState = system === 'update' || system === 'error' ? system : 'idle';
  const asking = observer.holds[ASK_KEY] === 'asking';
  const { settings, setSettings } = observer;
  // Проба падения — в активную вкладку окна в фокусе; одноразовая: ловит граница, и запрос снимается.
  const crashTab = () => {
    const path = focusedPath(ws.state);
    prefs.set({ crashAt: path });
    window.setTimeout(() => prefs.set((p) => (p.crashAt === path ? { crashAt: null } : {})), CRASH_WINDOW_MS);
  };
  return (
    <div className="flex shrink-0 flex-col gap-2 border-b border-line bg-canvas px-4 py-2">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
        <Link
          to="/design"
          className="inline-flex items-center gap-1.5 text-xs font-medium text-fg-muted hover:text-fg"
        >
          <ArrowLeft className="size-3.5" />
          Галерея
        </Link>
        <span className="text-sm font-semibold text-fg">Основа</span>
        <Segmented
          label="Второй уровень"
          value={prefs.sectionTone}
          onChange={(v) => prefs.set({ sectionTone: v })}
          options={[
            { value: 'dark', label: 'Тёмный' },
            { value: 'light', label: 'Светлый' },
          ]}
        />
        <Segmented
          label="Состояние системы"
          value={systemState}
          onChange={(v) => observer.setState(v)}
          options={SYSTEM_STATES}
        />
        <Switch
          size="sm"
          checked={prefs.colorByState}
          onCheckedChange={(colorByState) => prefs.set({ colorByState })}
          label="Окраска по состоянию"
        />
        <Switch
          size="sm"
          checked={prefs.dense}
          onCheckedChange={(dense) => prefs.set({ dense })}
          label="Плотно"
        />
        <Switch
          size="sm"
          checked={prefs.focus}
          onCheckedChange={(focus) => prefs.set({ focus })}
          label="Фокус · Ctrl+B"
        />
        <div className="ml-auto">
          <IconButton
            aria-label={mode === 'dark' ? 'Светлая тема' : 'Тёмная тема'}
            variant="secondary"
            size="sm"
            onClick={toggle}
          >
            {mode === 'dark' ? <Sun /> : <Moon />}
          </IconButton>
        </div>
      </div>
      {/* Ряд «Сервисы»: смена сценария образца, а не событие «подключено» — «показано» сразу равно срезу,
          строки и пластины потом не приезжают; открытый веер перестраивается правилами пачки. */}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
        <Segmented
          label="Сервисы"
          value={SERVICE_SAMPLES.some((o) => o.value === count) ? count : ''}
          onChange={(v) => {
            const slice = SERVICE_ORDER.slice(0, Number(v));
            prefs.set({
              services: slice,
              servicesOff: SAMPLE_OFF.filter((id) => slice.includes(id)),
              seenServices: slice,
              fanSeen: slice,
            });
          }}
          options={SERVICE_SAMPLES}
        />
        <Switch
          size="sm"
          checked={prefs.serviceAlert}
          onCheckedChange={(serviceAlert) => prefs.set({ serviceAlert })}
          label="Сбой у Slack"
        />
      </div>
      {/* Ряд «Ожидание и ошибки» (R42): темп загрузки; состояние вкладки целиком и проба падения — на
          активной вкладке окна в фокусе; нет связи; первая загрузка сайта. */}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 border-t border-line pt-2">
        <span className="text-xs font-medium text-fg-muted">Ожидание и ошибки</span>
        <Segmented
          label="Загрузка"
          value={prefs.loadPace}
          onChange={(loadPace) => prefs.set({ loadPace })}
          options={LOAD_PACES}
        />
        <Segmented
          label="Вкладка"
          value={prefs.tabDemo}
          onChange={(tabDemo) => prefs.set({ tabDemo })}
          options={TAB_DEMOS}
        />
        <Switch
          size="sm"
          checked={prefs.offline}
          onCheckedChange={(offline) => prefs.set({ offline })}
          label="Нет связи"
        />
        {/* Сцена первой загрузки поверх оболочки: «Быстро» играется в темпе «Медленно», «Сбой» встаёт на шаге 3. */}
        <Button
          size="sm"
          variant="ghost"
          leftIcon={<RotateCcw />}
          onClick={() => prefs.set({ bootAt: performance.now() })}
        >
          Первая загрузка
        </Button>
        <Button size="sm" variant="ghost" leftIcon={<Bug />} onClick={crashTab}>
          Уронить вкладку
        </Button>
      </div>
      {/* Ряд «Помощник» (spec-3 §9, spec-3-motion §11): режим, примерные события и спорные места —
          переключателями до ответа Максима (посадка, кольцо на цели); дорога и замедление — для проверки. */}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 border-t border-line pt-2">
        <span className="text-xs font-medium text-fg-muted">Помощник</span>
        <Segmented
          value={assistantMode(settings)}
          onChange={(m) => setSettings(assistantPatch(m))}
          options={ASSISTANT_MODES}
        />
        {/* Не модальное: модальное меню на миг закрывает страницу для мыши, и цель события «не видна». */}
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <Button size="sm" variant="secondary" leftIcon={<BellRing />} rightIcon={<ChevronDown />}>
              Событие
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            <DropdownMenuItem onSelect={() => emitSample('notification')}>
              Пришло уведомление
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => emitSample('done')}>Операция завершилась</DropdownMenuItem>
            <DropdownMenuItem onSelect={() => emitSample('ask')}>Нужен ответ</DropdownMenuItem>
            {asking && <DropdownMenuItem onSelect={() => emitSample('answer')}>Ответ дан</DropdownMenuItem>}
          </DropdownMenuContent>
        </DropdownMenu>
        <Segmented
          label="Посадка"
          value={settings.seat}
          onChange={(seat) => setSettings({ seat })}
          options={SEATS}
        />
        <Switch
          size="sm"
          checked={settings.targetRing}
          onCheckedChange={(targetRing) => setSettings({ targetRing })}
          label="Кольцо на цели"
        />
        <Switch
          size="sm"
          checked={settings.showRoute}
          onCheckedChange={(showRoute) => setSettings({ showRoute })}
          label="Показать дорогу"
        />
        <Switch
          size="sm"
          checked={settings.slowMo}
          onCheckedChange={(slowMo) => setSettings({ slowMo })}
          label="Замедлить ×4"
        />
      </div>
    </div>
  );
}

function Segmented<T extends string>({
  label,
  value,
  onChange,
  options,
}: {
  /** Подпись перед выбором; без неё — когда выбор и есть сам ряд («Помощник»). */
  label?: string;
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: string }[];
}) {
  return (
    <div className="flex items-center gap-2">
      {label && <span className="text-2xs font-medium text-fg-subtle uppercase">{label}</span>}
      <Tabs variant="pill" value={value} onValueChange={(v) => onChange(v as T)}>
        <TabsList>
          {options.map((o) => (
            <TabsTrigger key={o.value} value={o.value}>
              {o.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
    </div>
  );
}

/** Курсор в поле ввода — одиночные клавиши оболочки молчат. */
function isTyping(target: EventTarget | null) {
  return (
    target instanceof HTMLElement &&
    (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName))
  );
}

function Shell({ overlays }: { overlays: Overlays }) {
  const navigate = useNavigate();
  const ws = useWorkspace();
  const prefs = usePrefs();
  const observer = useObserver();
  const { toast } = useToast();
  const path = focusedPath(ws.state);
  const service = resolveFoundation(path).serviceId;
  const inApp = service.startsWith('app:');
  const mode: SideMode = prefs.focus || service === 'home' ? 'none' : inApp ? 'app' : 'section';

  // Уходящий уровень уезжает со своим содержимым: помним последний раздел и путь в нём.
  const [lastSection, setLastSection] = useState({ id: 'marketplace', path: at('/marketplace') });
  if (!inApp && service !== 'home' && (service !== lastSection.id || path !== lastSection.path)) {
    setLastSection({ id: service, path });
  }

  // Открытие приложения (R42, spec-2 §3.2). Первое за сеанс — холодное: шаги «Права доступа · Код
  // приложения · Меню · Первый вид»; вкладка показывает медальон по шагам (StateGate), у меню приложения —
  // заглушка той же формы (R31), пока не пройден шаг «Меню». Дальше — тёплое: меню сразу, у вида своя
  // заглушка. Главное меню при этом ведёт себя как всегда: загрузка — не состояние системы.
  const [opened, setOpened] = useState<ReadonlySet<string>>(() => new Set());
  const [run, setRun] = useState<AppRun | null>(null);
  // Сменили темп — следующее открытие снова холодное: его и хотят увидеть.
  const [pace, setPace] = useState(prefs.loadPace);
  if (pace !== prefs.loadPace) {
    setPace(prefs.loadPace);
    setOpened(new Set());
  }
  const appNow = inApp ? service : '';
  const [appWas, setAppWas] = useState('');
  if (appWas !== appNow) {
    setAppWas(appNow);
    // Ушли, не дождавшись, — открытие идёт дальше, как настоящая загрузка: вкладка, что его ждёт (хоть во
    // втором окне), досмотрит его до конца, а вернулись — оно на том же шаге. Бросает его только «Назад».
    if (appNow && !opened.has(appNow) && run?.service !== appNow) {
      setRun({
        service: appNow,
        pace: prefs.loadPace,
        done: 0,
        failed: false,
        retried: false,
        menuHeld: prefs.loadPace === 'fast',
        attempt: 0,
      });
    }
  }
  // Шаги идут таймерами; в темпе «Сбой» открытие встаёт на шаге «Меню», после «Повторить» проходит.
  // «Повторить» без сбоя («дольше обычного») начинает текущий шаг заново.
  const stepAt = run && !run.failed && run.done < APP_OPEN_STEPS.length ? run.done : -1;
  const runPace = run?.pace ?? 'fast';
  const runAttempt = run?.attempt ?? 0;
  useEffect(() => {
    if (stepAt < 0) return undefined;
    const t = window.setTimeout(
      () =>
        setRun((r) => {
          if (!r) return r;
          if (r.pace === 'fail' && r.done === MENU_STEP && !r.retried) return { ...r, failed: true };
          // Шаги в 0 мс проходят вместе с этим — одним обновлением, без лишних кадров.
          let done = r.done + 1;
          while (done < APP_OPEN_STEPS.length && APP_STEP_MS[r.pace][done] === 0) done += 1;
          return { ...r, done };
        }),
      APP_STEP_MS[runPace][stepAt],
    );
    return () => window.clearTimeout(t);
  }, [stepAt, runPace, runAttempt]);
  const menuHeld = run?.menuHeld ?? false;
  useEffect(() => {
    if (!menuHeld) return undefined;
    const t = window.setTimeout(() => setRun((r) => r && { ...r, menuHeld: false }), FAST_MENU_MS);
    return () => window.clearTimeout(t);
  }, [menuHeld]);
  // Все шаги пройдены и меню на месте — приложение открыто: дальше оно открывается тёпло.
  if (run && run.done >= APP_OPEN_STEPS.length && !run.menuHeld) {
    setRun(null);
    setOpened(new Set(opened).add(run.service));
  }
  const retryOpen = useCallback(
    () => setRun((r) => r && { ...r, failed: false, retried: r.retried || r.failed, attempt: r.attempt + 1 }),
    [],
  );

  // Главное меню раскрыто на главной и свёрнуто в разделе и в приложении; ручной выбор держится,
  // пока не сменился уровень. Ушли с главной при раскрытом меню — оно держится раскрытым, пока не
  // выбран пункт во втором или третьем уровне: подписи видны, пока человек осматривается в разделе.
  const [hold, setHold] = useState(false);
  if (hold && service === 'home') setHold(false);
  const [manual, setManual] = useState<{ mode: SideMode; expanded: boolean } | null>(null);
  const expanded =
    manual && manual.mode === mode ? manual.expanded : (mode === 'none' || hold) && !prefs.focus;

  // Переход после входа (страница /design/login): веер складывается, ядро летит домой,
  // рабочее место собирается. Меньше движения в системе — сразу рабочее место.
  const [entrance, setEntrance] = useState<Arrival | null>(() => {
    const from = prefersReducedMotion() ? null : peekEntrance();
    return from && { from, pace: 'scene' };
  });
  const [brandOpen, setBrandOpen] = useState(() => entrance === null);
  useEffect(() => clearEntrance(), []);
  // Меню и холст собираются следом только после входа; после первой загрузки оболочка уже стоит под сценой.
  const arriving = entrance?.pace === 'scene';

  // «Первая загрузка» (R42, spec-2 §3.1): сцена загрузки сайта поверх оболочки — как при настоящей загрузке,
  // оболочка под ней не отвечает. Приложения сеанса забыты; то, что на экране, загрузка и открывает («Первый
  // экран»). Знак в меню сложен и ждёт ядро: на «готово» оно прилетит из сцены (ShellEntrance pace="brisk").
  const [boot, setBoot] = useState<BootRun | null>(null);
  const [bootAt, setBootAt] = useState(prefs.bootAt);
  if (bootAt !== prefs.bootAt) {
    setBootAt(prefs.bootAt);
    if (prefs.bootAt !== null) {
      setBoot({
        startedAt: prefs.bootAt,
        fail: prefs.loadPace === 'fail',
        done: 1,
        failed: false,
        retried: false,
        attempt: 0,
      });
      setOpened(new Set(inApp ? [service] : []));
      // Приложение на экране загрузка и открывает: его открытие ею и кончается. Открытие другого (вкладка во
      // втором окне) идёт дальше под сценой.
      setRun((r) =>
        r && inApp && r.service === service
          ? { ...r, done: APP_OPEN_STEPS.length, failed: false, menuHeld: false }
          : r,
      );
      setEntrance(null);
      setBrandOpen(false);
    }
  }
  // Шаги — таймерами; без связи стоят; в темпе «Сбой» встаёт на шаге 3, после «Повторить» проходит.
  // «Повторить» без сбоя («дольше обычного») начинает текущий шаг заново.
  const bootStep = boot && !boot.failed && !prefs.offline && boot.done < BOOT_STEPS.length ? boot.done : -1;
  const bootAttempt = boot?.attempt ?? 0;
  useEffect(() => {
    if (bootStep < 0) return undefined;
    const t = window.setTimeout(
      () =>
        setBoot((b) => {
          if (!b) return b;
          if (b.fail && b.done === BOOT_FAIL_STEP && !b.retried) return { ...b, failed: true };
          return { ...b, done: b.done + 1 };
        }),
      BOOT_STEP_MS,
    );
    return () => window.clearTimeout(t);
  }, [bootStep, bootAttempt]);
  const retryBoot = useCallback(
    () =>
      setBoot((b) => b && { ...b, failed: false, retried: b.retried || b.failed, attempt: b.attempt + 1 }),
    [],
  );
  // Готово: ядро из сцены летит домой; сцена так и не показалась (или меньше движения) — веер в меню сразу.
  const onBootReady = (from: EntranceFrom | null) => {
    setBoot(null);
    if (from) setEntrance({ from, pace: 'brisk' });
    else setBrandOpen(true);
  };
  const booting = boot !== null;

  const { search, setSearch, notifications, setNotifications, ai, setAi } = overlays;
  // Непрочитанные — состояние оболочки: счётчик колокольчика растёт сразу, без ядра (spec-3 §4а).
  const [unread, setUnread] = useState(4);
  // Экранам — команды и состояние оболочки: «Найти · Ctrl+K» на «не найдено» открывает тот же поиск;
  // вкладки приложения видят его холодное открытие, «Назад» ведёт туда же, куда «←» меню приложения.
  const { go } = ws;
  const backPath = lastSection.path;
  const commands = useMemo<ShellCommands>(
    () => ({
      openSearch: () => setSearch(true),
      unread,
      opening: run && {
        service: run.service,
        name: APP_OPEN_NAME[run.service] ?? '',
        steps: APP_OPEN_STEPS,
        done: run.done,
        failed: run.failed,
        retry: retryOpen,
        // «Назад» бросает открытие: в следующий раз приложение снова откроется холодно.
        back: () => {
          setRun(null);
          go(backPath);
        },
      },
    }),
    [run, retryOpen, go, backPath, setSearch, unread],
  );

  // Примерные события, которые не обработал экран (sampleEvents.ts): признаки ставит оболочка — сразу и без
  // ядра, — а помощник показывает, где это. «Операция завершилась» без идущего обслуживания: 2,4 с
  // операция (ядро плавно синее), затем «готово»; места не видно — тост «Открыть», и читалке молчит строка.
  const { notify, hold: holdState, resolve, anchor } = observer;
  const sampleTimers = useRef<number[]>([]);
  useEffect(() => {
    const timers = sampleTimers.current;
    const off = onSampleFallback((kind) => {
      if (kind === 'notification') {
        setUnread((u) => u + 1);
        notify({ kind: 'notification', key: 'notification', anchorId: NOTIFY_ANCHOR });
      } else if (kind === 'ask') {
        // Самого INC-2189 не видно — тост «Открыть»: к ответу за одно нажатие; читалке тогда говорит тост.
        const place = anchor('obj.INC-2189');
        const seen = !!place && isAnchorVisible(place);
        if (!seen) {
          toast({
            title: 'INC-2189 ждёт подтверждения',
            action: { label: 'Открыть', onClick: () => go(ASK_PATH) },
          });
        }
        notify({
          kind: 'ask',
          key: ASK_KEY,
          anchorId: ASK_ANCHORS,
          label: 'INC-2189 ждёт подтверждения',
          announce: seen,
        });
      } else if (kind === 'answer') {
        resolve(ASK_KEY);
      } else {
        holdState(OP_KEY, 'update');
        timers.push(
          window.setTimeout(() => {
            const place = anchor(OP_KEY);
            const seen = !!place && isAnchorVisible(place);
            if (!seen) {
              toast({
                title: 'host-338: обслуживание завершено',
                tone: 'success',
                action: { label: 'Открыть', onClick: () => go(OP_PATH) },
              });
            }
            notify({
              kind: 'done',
              key: OP_KEY,
              anchorId: OP_ANCHORS,
              label: 'host-338: обслуживание завершено',
              announce: seen,
            });
          }, SAMPLE_OP_MS),
        );
      }
    });
    return () => {
      off();
      timers.forEach((t) => window.clearTimeout(t));
    };
  }, [notify, holdState, resolve, anchor, toast, go]);
  // Вкладку поймала её граница — проба «Уронить вкладку» сработала, снимаем её.
  const { set: setPrefs } = prefs;
  const onTabError = useCallback(
    (crash: TabCrash) => setPrefs((p) => (p.crashAt === crash.path ? { crashAt: null } : {})),
    [setPrefs],
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Идёт первая загрузка — оболочки для человека ещё нет.
      if (booting) return;
      const mod = e.ctrlKey || e.metaKey;
      if (mod && e.code === 'KeyK') setSearch((v) => !v);
      else if (mod && e.code === 'KeyJ') setAi((v) => !v);
      else if (mod && e.code === 'KeyB') prefs.set({ focus: !prefs.focus });
      // «[» — свернуть или раскрыть главное меню. По месту клавиши: в русской раскладке это «х».
      // Не в полях ввода и не в режиме фокуса; ручной выбор, держание снимается — как у ручки на кромке.
      else if (
        e.code === 'BracketLeft' &&
        !mod &&
        !e.altKey &&
        !e.shiftKey &&
        !e.repeat &&
        !prefs.focus &&
        !isTyping(e.target)
      ) {
        setHold(false);
        setManual({ mode, expanded: !expanded });
      } else if (e.key === 'Escape') {
        setSearch(false);
        setAi(false);
        setNotifications(false);
        return;
      } else return;
      e.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [prefs, mode, expanded, booting, setSearch, setAi, setNotifications]);

  // Закреп «Инфраструктуры» — только у подключённой и включённой (spec-4 §10.3). Пока висит «нужен ответ»,
  // на закрепе точка и подсказка — признак ставится сразу и без ядра (spec-3 §4а).
  const infraOn = prefs.services.includes('infra') && !prefs.servicesOff.includes('infra');
  const infraPinned = prefs.pinnedInfra && infraOn;
  const infraItem =
    observer.holds[ASK_KEY] === 'asking'
      ? { ...INFRA_PIN, badge: 'dot' as const, hint: `${INFRA_PIN.label} · ждёт ответа` }
      : INFRA_PIN;
  const activeId = service === 'home' ? null : inApp ? (infraPinned ? INFRA_PIN.id : 'marketplace') : service;
  const sectionDef = SECTION_DEFS[lastSection.id] ?? SECTION_DEFS.marketplace;
  const section =
    sectionDef.id === 'marketplace'
      ? { ...sectionDef, groups: marketplaceGroups(prefs.services) }
      : sectionDef;

  // Профиль и настройки аккаунта — из окна «я»; уход с главной держит меню раскрытым, как пункт меню.
  const goSettings = (to: string) => {
    if (service === 'home' && expanded) setHold(true);
    ws.go(at(to));
  };

  const onMainSelect = (id: string) => {
    if (id === 'ai') return setAi(true);
    if (id === 'notifications') return setNotifications((v) => !v);
    if (service === 'home' && expanded) setHold(true);
    if (id === 'marketplace') ws.go(lastSection.id === 'marketplace' ? lastSection.path : at('/marketplace'));
    else if (id === 'settings') ws.go(at('/settings/profile'));
    else if (id === INFRA_PIN.id) ws.go(at('/apps/infra'));
  };

  // Сцена первой загрузки и переход после неё — над оболочкой, но под верхней полосой: полоса — пульт образца,
  // а не часть сайта, её переключатели («Нет связи», темп) работают и во время загрузки.
  return (
    <div data-shell="" className="relative flex min-h-0 flex-1 flex-col">
      <div inert={booting} className="flex min-h-0 flex-1 gap-3 p-3">
        <div
          style={arriving ? { animationDelay: '620ms' } : undefined}
          className={cn(
            'flex shrink-0 overflow-hidden transition-width',
            prefs.focus ? '-mr-3 w-0' : expanded ? 'w-60' : 'w-20',
            arriving && 'animate-enter-fade',
          )}
        >
          <MainMenu
            expanded={expanded}
            onToggleExpand={() => {
              setHold(false);
              setManual({ mode, expanded: !expanded });
            }}
            homeActive={service === 'home'}
            onHome={() => ws.go(HOME)}
            activeId={activeId}
            sections={SECTIONS}
            pinned={infraPinned ? [infraItem] : []}
            tools={TOOLS(unread)}
            onSelect={onMainSelect}
            tone={prefs.colorByState ? toneOf(observer.state) : 'brand'}
            hidden={prefs.focus}
            brandOpen={brandOpen}
            entering={arriving}
            life="both"
            account={{
              name: 'Демо Пользователь',
              email: 'demo@example.test',
              onProfile: () => goSettings('/settings/profile'),
              onAccountSettings: () => goSettings('/settings/security'),
              onLogout: () => navigate('/design/login'),
            }}
          />
        </div>

        <SideZone
          mode={mode}
          section={
            <SectionMenu
              title={section.title}
              groups={section.groups}
              activeKey={lastSection.id === service ? menuKeyOf(path) : null}
              tone={prefs.sectionTone}
              onSelect={(item) => {
                setHold(false);
                ws.go(item.key === 'all' ? at(section.base) : at(`${section.base}/${item.key}`));
              }}
            />
          }
          app={
            <AppMenu
              app={INFRA_APP}
              status={
                <span className="inline-flex items-center gap-1.5">
                  <span className="size-1.5 animate-live-dot rounded-full bg-success" aria-hidden />
                  Метрики · 12 с
                </span>
              }
              groups={INFRA_GROUPS}
              manage={INFRA_MANAGE}
              activeKey={inApp ? menuKeyOf(path) : null}
              loading={run !== null && (run.done <= MENU_STEP || run.menuHeld)}
              stalled={run?.failed}
              onSelect={(item) => {
                setHold(false);
                ws.go(infraPath(item.key));
              }}
              onBack={() => ws.go(lastSection.path)}
              backLabel={section.title}
              searchPlaceholder="Найти узел или машину"
              footer="71 узел · 12 локаций · 9 842 VPS"
            />
          }
        />

        <div
          style={arriving ? { animationDelay: '900ms' } : undefined}
          className={cn('flex min-w-0 flex-1', arriving && 'animate-enter-rise')}
        >
          <ShellCommandsContext.Provider value={commands}>
            <WorkspaceCanvas
              routes={routes}
              onTabError={onTabError}
              notice={<ConnectionStrip offline={prefs.offline} dataAt={OFFLINE_DATA_AT} />}
            />
          </ShellCommandsContext.Provider>
        </div>

        {entrance && (
          <ShellEntrance
            from={entrance.from}
            pace={entrance.pace}
            contained={entrance.pace === 'brisk'}
            onLand={() => setBrandOpen(true)}
            onDone={() => setEntrance(null)}
          />
        )}

        {prefs.focus && (
          <Tooltip content="Показать меню · Ctrl+B" side="right">
            <IconButton
              aria-label="Показать меню"
              variant="secondary"
              className="fixed bottom-4 left-4 z-dock"
              onClick={() => prefs.set({ focus: false })}
            >
              <Menu />
            </IconButton>
          </Tooltip>
        )}

        <FloatingSearchDock
          expanded={search}
          onOpen={() => setSearch(true)}
          onClose={() => setSearch(false)}
        />
        <NotificationsDrawer open={notifications} onClose={() => setNotifications(false)} />
        <AISpotlight open={ai} onClose={() => setAi(false)} />
        {/* Кнопка ИИ стоит выше слоя сцен — пока идёт загрузка, оболочки для человека ещё нет. */}
        {!booting && <AIFab onOpen={() => setAi(true)} />}
      </div>
      {/* Сцена — вне оболочки: та под ней не отвечает (inert), а кнопки сцены — отвечают. */}
      {boot && (
        <BootScene
          contained
          key={boot.startedAt}
          steps={BOOT_STEPS}
          done={boot.done}
          failed={boot.failed ? BOOT_FAILURE : null}
          offline={prefs.offline}
          startedAt={boot.startedAt}
          blank
          onRetry={retryBoot}
          onExit={() => navigate('/design/login')}
          onReady={onBootReady}
        />
      )}
    </div>
  );
}
